import { Command } from "commander";
import { password } from "@inquirer/prompts";
import chalk from "chalk";
import { promises as fs } from "node:fs";
import os from "node:os";
import path from "node:path";
import { getConfig } from "../config/index.js";

const MCP_SERVER_NAME = "coAligne";

// Config file paths for each agent platform
const CODEX_CONFIG_PATH = path.join(os.homedir(), ".codex", "config.toml");
const GEMINI_CONFIG_PATH = path.join(
  os.homedir(),
  ".gemini",
  "config",
  "mcp_config.json",
);
const CLAUDE_CODE_CONFIG_PATH = path.join(os.homedir(), ".claude.json");
const CLAUDE_DESKTOP_CONFIG_PATH =
  os.platform() === "darwin"
    ? path.join(
        os.homedir(),
        "Library",
        "Application Support",
        "Claude",
        "claude_desktop_config.json",
      )
    : path.join(
        process.env.APPDATA ||
        path.join(os.homedir(), "AppData", "Roaming"),
        "Claude",
        "claude_desktop_config.json",
      );
const WORKBUDDY_CONFIG_PATH = path.join(os.homedir(), ".workbuddy", "mcp.json");

/**
 * Get the MCP server URL dynamically from stored config.
 */
function getMcpUrl(): string {
  const config = getConfig();
  const serverUrl = config.serverUrl || "https://app.coaligne.com";
  return `${serverUrl.replace(/\/+$/, "")}/mcp`;
}

/**
 * Get the MCP SSE server URL (backward compatibility).
 */
function getMcpSseUrl(): string {
  const config = getConfig();
  const serverUrl = config.serverUrl || "https://app.coaligne.com";
  return `${serverUrl.replace(/\/+$/, "")}/mcp/sse`;
}

// ─── Shared helpers ──────────────────────────────────────────────────

/**
 * Read a JSON config file, returning {} if it doesn't exist or is invalid.
 */
async function readJsonConfig(filePath: string): Promise<any> {
  try {
    const raw = await fs.readFile(filePath, "utf-8");
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

/**
 * Build the mcp-remote entry used by Claude Code, Claude Desktop, and WorkBuddy.
 */
function buildMcpRemoteEntry(
  accessToken: string,
  mcpUrl: string,
): Record<string, unknown> {
  return {
    command: "npx",
    args: [
      "-y",
      "mcp-remote",
      mcpUrl,
      "--header",
      `Authorization: Bearer \${COALIGNE_ACCESS_TOKEN}`,
    ],
    env: {
      COALIGNE_ACCESS_TOKEN: accessToken,
    },
  };
}

/**
 * Build the TOML block for the coAligne MCP server section (Codex).
 */
function buildCodexMcpSection(mcpUrl: string): string {
  return [
    `[mcp_servers.${MCP_SERVER_NAME}]`,
    `enabled = true`,
    `url = "${mcpUrl}"`,
    `bearer_token_env_var = "COALIGNE_ACCESS_TOKEN"`,
  ].join("\n");
}

/**
 * Insert or replace the [mcp_servers.coAligne] block in existing TOML content.
 */
function upsertCodexMcpSection(
  existing: string,
  newSection: string,
): string {
  const pattern = /\[mcp_servers\.coAligne\][^\[]*/s;

  if (pattern.test(existing)) {
    return existing.replace(pattern, newSection + "\n\n");
  }

  const trimmed = existing.trimEnd();
  return trimmed ? `${trimmed}\n\n${newSection}\n` : `${newSection}\n`;
}

/**
 * Remove coAligne MCP entry from a JSON config file.
 */
async function uninstallJsonMcpConfig(
  filePath: string,
  label: string,
  opts?: { quiet?: boolean },
): Promise<void> {
  try {
    let raw = "";
    try {
      raw = await fs.readFile(filePath, "utf-8");
    } catch {
      return;
    }

    const config = JSON.parse(raw);
    if (config.mcpServers && config.mcpServers[MCP_SERVER_NAME]) {
      delete config.mcpServers[MCP_SERVER_NAME];
      await fs.writeFile(
        filePath,
        JSON.stringify(config, null, 2),
        "utf-8",
      );
      if (!opts?.quiet) {
        console.log(
          chalk.green(
            `✓ 已从 ${filePath} 移除 coAligne MCP 服务配置`,
          ),
        );
      }
    }
  } catch (err) {
    if (!opts?.quiet) {
      console.warn(
        chalk.yellow(
          `清理 ${label} MCP 配置失败: ${err instanceof Error ? err.message : String(err)}`,
        ),
      );
    }
  }
}

/**
 * Show coAligne MCP config from a JSON config file if present.
 */
async function showJsonMcpConfig(
  filePath: string,
  label: string,
): Promise<boolean> {
  try {
    const content = await fs.readFile(filePath, "utf-8");
    const config = JSON.parse(content);
    if (config.mcpServers && config.mcpServers[MCP_SERVER_NAME]) {
      console.log(chalk.blue(`当前 ${label} 配置：`));
      const display = {
        mcpServers: {
          [MCP_SERVER_NAME]: config.mcpServers[MCP_SERVER_NAME],
        },
      };
      console.log(JSON.stringify(display, null, 2));
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

// ─── Per-platform install functions ──────────────────────────────────

/**
 * Writes or replaces the [mcp_servers.coAligne] section in ~/.codex/config.toml.
 */
async function installCodexMcpConfig(
  accessToken: string,
  mcpUrl: string,
  opts?: { quiet?: boolean },
): Promise<void> {
  const newSection = buildCodexMcpSection(mcpUrl);

  try {
    let existing = "";
    try {
      existing = await fs.readFile(CODEX_CONFIG_PATH, "utf-8");
    } catch {
      // File doesn't exist yet — will be created
    }

    const updated = upsertCodexMcpSection(existing, newSection);
    await fs.mkdir(path.dirname(CODEX_CONFIG_PATH), { recursive: true });
    await fs.writeFile(CODEX_CONFIG_PATH, updated, "utf-8");

    if (!opts?.quiet) {
      console.log(
        chalk.green(`✓ MCP 服务已配置到 ${CODEX_CONFIG_PATH}`),
      );
    }
  } catch (err) {
    if (!opts?.quiet) {
      console.error(
        chalk.red("配置 Codex MCP 失败:"),
        err instanceof Error ? err.message : String(err),
      );
    }
  }
}

/**
 * Writes coAligne MCP config into ~/.gemini/config/mcp_config.json.
 */
export async function installGeminiMcpConfig(
  accessToken: string,
  mcpUrl: string,
  opts?: { quiet?: boolean },
): Promise<void> {
  try {
    const existing = await readJsonConfig(GEMINI_CONFIG_PATH);

    if (!existing.mcpServers) {
      existing.mcpServers = {};
    }

    existing.mcpServers[MCP_SERVER_NAME] = {
      url: mcpUrl,
      bearerTokenEnvVar: "COALIGNE_ACCESS_TOKEN",
    };

    await fs.mkdir(path.dirname(GEMINI_CONFIG_PATH), { recursive: true });
    await fs.writeFile(
      GEMINI_CONFIG_PATH,
      JSON.stringify(existing, null, 2),
      "utf-8",
    );
    if (!opts?.quiet) {
      console.log(
        chalk.green(`✓ MCP 服务已配置到 ${GEMINI_CONFIG_PATH}`),
      );
    }
  } catch (err) {
    if (!opts?.quiet) {
      console.error(
        chalk.red("配置 Gemini MCP 失败:"),
        err instanceof Error ? err.message : String(err),
      );
    }
  }
}

/**
 * Writes coAligne MCP config into ~/.claude.json for Claude Code (terminal CLI).
 * Uses mcp-remote as a stdio→SSE bridge.
 */
export async function installClaudeCodeMcpConfig(
  accessToken: string,
  mcpUrl: string,
  opts?: { quiet?: boolean },
): Promise<void> {
  try {
    const existing = await readJsonConfig(CLAUDE_CODE_CONFIG_PATH);

    if (!existing.mcpServers) {
      existing.mcpServers = {};
    }

    existing.mcpServers[MCP_SERVER_NAME] = buildMcpRemoteEntry(
      accessToken,
      mcpUrl,
    );

    await fs.writeFile(
      CLAUDE_CODE_CONFIG_PATH,
      JSON.stringify(existing, null, 2),
      "utf-8",
    );
    if (!opts?.quiet) {
      console.log(
        chalk.green(
          `✓ MCP 服务已配置到 ${CLAUDE_CODE_CONFIG_PATH}`,
        ),
      );
    }
  } catch (err) {
    if (!opts?.quiet) {
      console.error(
        chalk.red("配置 Claude Code MCP 失败:"),
        err instanceof Error ? err.message : String(err),
      );
    }
  }
}

/**
 * Writes coAligne MCP config into Claude Desktop's config file.
 * Uses mcp-remote as a stdio→SSE bridge.
 */
export async function installClaudeDesktopMcpConfig(
  accessToken: string,
  mcpUrl: string,
  opts?: { quiet?: boolean },
): Promise<void> {
  try {
    const existing = await readJsonConfig(CLAUDE_DESKTOP_CONFIG_PATH);

    if (!existing.mcpServers) {
      existing.mcpServers = {};
    }

    existing.mcpServers[MCP_SERVER_NAME] = buildMcpRemoteEntry(
      accessToken,
      mcpUrl,
    );

    await fs.mkdir(path.dirname(CLAUDE_DESKTOP_CONFIG_PATH), {
      recursive: true,
    });
    await fs.writeFile(
      CLAUDE_DESKTOP_CONFIG_PATH,
      JSON.stringify(existing, null, 2),
      "utf-8",
    );
    if (!opts?.quiet) {
      console.log(
        chalk.green(
          `✓ MCP 服务已配置到 ${CLAUDE_DESKTOP_CONFIG_PATH}`,
        ),
      );
    }
  } catch (err) {
    if (!opts?.quiet) {
      console.error(
        chalk.red("配置 Claude Desktop MCP 失败:"),
        err instanceof Error ? err.message : String(err),
      );
    }
  }
}

/**
 * Writes coAligne MCP config into WorkBuddy's config file.
 * Uses mcp-remote as a stdio→SSE bridge.
 */
export async function installWorkBuddyMcpConfig(
  accessToken: string,
  mcpUrl: string,
  opts?: { quiet?: boolean },
): Promise<void> {
  try {
    const existing = await readJsonConfig(WORKBUDDY_CONFIG_PATH);

    if (!existing.mcpServers) {
      existing.mcpServers = {};
    }

    existing.mcpServers[MCP_SERVER_NAME] = buildMcpRemoteEntry(
      accessToken,
      mcpUrl,
    );

    await fs.mkdir(path.dirname(WORKBUDDY_CONFIG_PATH), {
      recursive: true,
    });
    await fs.writeFile(
      WORKBUDDY_CONFIG_PATH,
      JSON.stringify(existing, null, 2),
      "utf-8",
    );
    if (!opts?.quiet) {
      console.log(
        chalk.green(
          `✓ MCP 服务已配置到 ${WORKBUDDY_CONFIG_PATH}`,
        ),
      );
    }
  } catch (err) {
    if (!opts?.quiet) {
      console.error(
        chalk.red("配置 WorkBuddy MCP 失败:"),
        err instanceof Error ? err.message : String(err),
      );
    }
  }
}

// ─── Master install / uninstall ──────────────────────────────────────

/**
 * Master install: writes coAligne MCP config to all supported agent platforms.
 */
export async function installMcpConfig(
  accessToken: string,
  opts?: { quiet?: boolean },
): Promise<void> {
  const mcpUrl = getMcpUrl();
  await installCodexMcpConfig(accessToken, mcpUrl, opts);
  await installGeminiMcpConfig(accessToken, mcpUrl, opts);
  await installClaudeCodeMcpConfig(accessToken, mcpUrl, opts);
  await installClaudeDesktopMcpConfig(accessToken, mcpUrl, opts);
  await installWorkBuddyMcpConfig(accessToken, mcpUrl, opts);
}

export async function uninstallMcpConfig(
  opts?: { quiet?: boolean },
): Promise<void> {
  // Codex (TOML)
  try {
    let existing = "";
    try {
      existing = await fs.readFile(CODEX_CONFIG_PATH, "utf-8");
    } catch {
      // ignore
    }

    if (existing) {
      const pattern = /\[mcp_servers\.coAligne\][^\[]*/s;
      if (pattern.test(existing)) {
        const updated =
          existing.replace(pattern, "").trimEnd() + "\n";
        await fs.writeFile(CODEX_CONFIG_PATH, updated, "utf-8");
        if (!opts?.quiet) {
          console.log(
            chalk.green(
              `✓ 已从 ${CODEX_CONFIG_PATH} 移除 coAligne MCP 服务配置`,
            ),
          );
        }
      }
    }
  } catch (err) {
    if (!opts?.quiet) {
      console.warn(
        chalk.yellow(
          `清理 Codex MCP 配置失败: ${err instanceof Error ? err.message : String(err)}`,
        ),
      );
    }
  }

  // JSON-based platforms
  await uninstallJsonMcpConfig(GEMINI_CONFIG_PATH, "Gemini", opts);
  await uninstallJsonMcpConfig(
    CLAUDE_CODE_CONFIG_PATH,
    "Claude Code",
    opts,
  );
  await uninstallJsonMcpConfig(
    CLAUDE_DESKTOP_CONFIG_PATH,
    "Claude Desktop",
    opts,
  );
  await uninstallJsonMcpConfig(
    WORKBUDDY_CONFIG_PATH,
    "WorkBuddy",
    opts,
  );
}

// ─── CLI command registration ────────────────────────────────────────

export function registerMcpCommand(program: Command): void {
  const mcpCmd = program
    .command("mcp")
    .description("管理 coAligne MCP 服务配置");

  mcpCmd
    .command("install")
    .description(
      "将 coAligne MCP 服务写入 Codex、Gemini、Claude Code、Claude Desktop 和 WorkBuddy 配置中",
    )
    .option(
      "-k, --access-token <token>",
      "Access Token（直接传入，跳过交互式提示）",
    )
    .option("--api-key <token>", "Access Token（旧参数别名，仍兼容）")
    .option("--quiet", "减少输出")
    .action(async (opts) => {
      let accessToken: string | null | undefined =
        opts.accessToken ?? opts.apiKey;

      // In quiet mode (e.g. postinstall), try to read key from stored config
      if (!accessToken && opts.quiet) {
        accessToken = getConfig().accessToken;
        if (!accessToken) {
          // No access token available yet — silently skip; login will configure MCP later
          return;
        }
      }

      const mcpUrl = getMcpUrl();
      const mcpSseUrl = getMcpSseUrl();

      if (!opts.quiet) {
        console.log(chalk.blue("配置 coAligne MCP 服务…"));
        console.log(chalk.gray(`  Streamable-HTTP: ${mcpUrl}`));
        console.log(chalk.gray(`  SSE (兼容): ${mcpSseUrl}`));
        console.log();
      }

      // Interactive mode: prompt for key
      if (!accessToken) {
        accessToken = await password({
          message: "请输入 coAligne Access Token",
          mask: "*",
        });
      }

      if (!accessToken || !accessToken.trim()) {
        console.error(chalk.red("Access Token 不能为空，已取消。"));
        process.exit(1);
      }

      await installMcpConfig(accessToken.trim(), { quiet: opts.quiet });

      if (!opts.quiet) {
        console.log();
        console.log(
          chalk.yellow(
            "提示: 请确保设置了环境变量 COALIGNE_ACCESS_TOKEN。",
          ),
        );
        if (os.platform() === "darwin") {
          console.log(
            chalk.gray(
              `  如需在 macOS GUI App 中生效，可运行：`,
            ),
          );
          console.log(
            chalk.blue(
              `  launchctl setenv COALIGNE_ACCESS_TOKEN "${accessToken.trim()}"`,
            ),
          );
        } else {
          console.log(
            chalk.gray(
              `  可以在 shell 配置文件（如 ~/.bashrc 或 ~/.zshrc）中添加：`,
            ),
          );
          console.log(
            chalk.blue(
              `  export COALIGNE_ACCESS_TOKEN="${accessToken.trim()}"`,
            ),
          );
        }
        console.log();
        console.log(
          chalk.green(
            "✓ 配置完成！重启各 Agent 后即可使用 coAligne MCP 工具。",
          ),
        );
      }
    });

  mcpCmd
    .command("show")
    .description("显示当前 coAligne MCP 服务配置")
    .action(async () => {
      let found = false;

      // Codex (TOML)
      try {
        const content = await fs.readFile(CODEX_CONFIG_PATH, "utf-8");
        const pattern = /\[mcp_servers\.coAligne\][^\[]*/s;
        const match = content.match(pattern);
        if (match) {
          console.log(chalk.blue("当前 Codex 配置："));
          console.log(match[0].trim());
          found = true;
        }
      } catch {
        // ignore
      }

      // Gemini
      if (found) console.log();
      if (await showJsonMcpConfig(GEMINI_CONFIG_PATH, "Gemini")) {
        found = true;
      }

      // Claude Code
      if (found) console.log();
      if (
        await showJsonMcpConfig(
          CLAUDE_CODE_CONFIG_PATH,
          "Claude Code",
        )
      ) {
        found = true;
      }

      // Claude Desktop
      if (found) console.log();
      if (
        await showJsonMcpConfig(
          CLAUDE_DESKTOP_CONFIG_PATH,
          "Claude Desktop",
        )
      ) {
        found = true;
      }

      // WorkBuddy
      if (found) console.log();
      if (await showJsonMcpConfig(WORKBUDDY_CONFIG_PATH, "WorkBuddy")) {
        found = true;
      }

      if (!found) {
        console.log(
          chalk.yellow(
            "未找到 coAligne MCP 配置。运行 `coaligne mcp install` 进行配置。",
          ),
        );
      }
    });

  mcpCmd
    .command("uninstall")
    .description(
      "从 Codex、Gemini、Claude Code、Claude Desktop 和 WorkBuddy 配置中移除 coAligne MCP 服务配置",
    )
    .option("--quiet", "减少输出")
    .action(async (opts) => {
      await uninstallMcpConfig(opts);
    });
}
